<template>
  <div class="card" style="min-height: calc(100vh - 158px)">
    <div class="p-2 bg-blue-light">
      <TitleButton
          :showBtn="false"
          title="Statement of income and expenditure"
          :show-filter-btn="true"
          @onClickFilterButton="onClickFilterButton"
      />
      <DateQuerySetter class="mt-2" btnTitle="Go" @onClickGo="onClickGo">
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Business</label>
          <v-select
            placeholder="Select Business"
            class="w-100"
            :options="businesses"
            label="name"
            :reduce="(name) => name.id"
            v-model="businessId"
          />
        </div>
        <div class="cols-12 col-sm-6 col-lg-4 col-xxl-3">
          <label for="colFormLabel" class="col-form-label">Project</label>
          <v-select
              placeholder="Select Project"
              v-model="projectId"
              :options="projects"
              label="name"
              :reduce="name => name.id"
              @option:selected="onSelectProject(true)"
          />
        </div>
        <div class="cols-12 col-sm-6 col-lg-4 col-xxl-3">
          <label for="colFormLabel" class="col-form-label">Donors</label>
          <v-select
              placeholder="Select Donor"
              class="w-100"
              :options="donors"
              label="name"
              :reduce="name => name.id"
              v-model="donorId"
          />
        </div>
        <div class="cols-12 col-sm-6 col-lg-4 col-xxl-3">
          <label for="colFormLabel" class="col-form-label">Cost Centre</label>
          <v-select
              placeholder="Select Cost Centre"
              v-model="costCentreId"
              :options="costCentres"
              label="name"
              :reduce="name => name.id" 
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">

          <div class="d-flex align-items-center justify-content-between filter-label">
            <label for="colFormLabel" class="col-form-label">Party: </label>
            <span class="filter-search-icon cursor-pointer" @click="onOpenContactSearchModal">
                    <i class="fas fa-search"></i>
                </span>
          </div>

          <AsyncSelect
            placeholder="Select Party"
            v-model="contactProfile"
            :api-service="fetchContactProfiles"
            :format-label="formatPatientLabel"
          />
        </div>
      </DateQuerySetter>
    </div>

    <div class="mt-2"></div>
    <div class="">
      <button class="btn btn-success float-end me-4" @click="exportProfitLoss">Export</button>
    </div>
    <div
      class="d-flex justify-content-between bg-white sticky px-2 mx-auto z-999 max-width-640 w-100"
      :class="{
        'opacity-50': accountingLoading,
        'opacity-100': !accountingLoading
      }"
    >
      <p class="cursor-pointer text-decoration-underline fw-bolder text-primary" @click="expandAll">
        {{ isExpanded ? 'Collapse All' : 'Expand All' }}
      </p>
    </div>
    <div
      class="d-flex justify-content-between bordered bg-white sticky py-1 px-2 border-t border-b mx-auto z-999 max-width-640 w-100"
      :class="{
        'opacity-50': accountingLoading,
        'opacity-100': !accountingLoading
      }"
    >
      <strong class="font-medium-2">Particulars</strong>
      <strong class="font-medium-2">Amount</strong>
    </div>
    <!-- income -->
    <div
      class="card px-2 mx-auto mt-1 max-width-640 w-100"
      :class="{
        'opacity-50': accountingLoading,
        'opacity-100': !accountingLoading
      }"
    >
      <span> Income </span>
      <BalanceSheetTree
        v-for="(assetItem, i) in income"
        :key="i"
        :node="assetItem"
        ref="assetTree"
      />
      <div class="d-flex justify-content-between border-b-2-dark node-hover">
        <strong>TOTAL INCOME</strong>
        <strong>{{ commaFormat(getTotal(income), noOfDecimals) }}</strong>
      </div>
    </div>
    <!-- income -->

    <!-- expense -->
    <div
      class="card px-2 mx-auto max-width-640 w-100"
      :class="{
        'opacity-50': accountingLoading,
        'opacity-100': !accountingLoading
      }"
    >
      <span> Expense </span>
      <BalanceSheetTree
        v-for="(equityItem, i) in expense"
        :key="i"
        :node="equityItem"
        ref="equityTree"
      />
      <div class="d-flex justify-content-between border-b-2-dark node-hover">
        <strong>TOTAL EXPENSE</strong>
        <strong>{{ commaFormat(getTotal(expense), noOfDecimals) }}</strong>
      </div>
      <div
        class="d-flex justify-content-between border-b-2-dark node-hover mt-2"
      >
        <strong>NET PROFIT / (LOSS)</strong>
        <strong>
            {{ commaFormat((getTotal(income) - getTotal(expense)), noOfDecimals) }}
        </strong>
      </div>
    </div>
    <!-- expense -->
    <Loader v-if="accountingLoading" />
    <ContactDropdownFilter
      v-if="store.state.isModalOpenThree"
      @onSearchContact="onSearchContact"
      :isFilterable="true"
      :companyRoles="companyRoles"
    />
    <FilterModal v-if="isModalOpen" @on-filter="onFilter"/>
  </div>
</template>

<script>
import figureFormatter from "@/services/utils/figureFormatter";
import handleAccounting from "@/services/modules/accounting";
import { inject } from "vue";

import TitleButton from "@/components/atom/TitleButton";
import Loader from "@/components/atom/LoaderComponent";
import DateQuerySetter from "@/components/atom/DateQuerySetter";
import BalanceSheetTree from "@/components/molecule/company/balance-sheet/BalanceSheetTree";
import handleCBusinesses from "@/services/modules/businesses";
import handleProjects from "@/services/modules/procurement/project";
import handleCostCentres from "@/services/modules/ngo/costCentre";
import handleContact from '@/services/modules/contact'
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import {useAsyncDropdownHelper} from "@/services/utils/asyncDropdownHelper";
import ContactDropdownFilter from "@/components/atom/Contact/ContactDropdownFilter.vue";
import Pagination from "@/components/atom/Pagination.vue";
import {useStore} from "vuex";
import handleRole from "@/services/modules/role";
import FilterModal from "@/components/atom/FilterModal.vue";

export default {
  name: "BalanceSheet",

  components: {
    FilterModal,
    Pagination,
    ContactDropdownFilter,
    AsyncSelect,
    TitleButton,
    DateQuerySetter,
    BalanceSheetTree,
    Loader,
  },

  data: () => ({
    businesses: [],
    projects : [],
    donors : [],
    costCentres : [],
    projectId: null,
    donorId: null,
    costCentreId: null,
    contactProfiles: [],
    businessId: null,
    balanceSheetData: [],
    contactProfile: null,
    companyRoles: []
  }),

  computed: {
    companyId() {
      return this.$route.params.companyId;
    },
    start() {
      return this.$route.query.start;
    },
    end() {
      return this.$route.query.end;
    },
    income() {
      return this.balanceSheetData.income ? this.balanceSheetData.income : [];
    },
    expense() {
      return this.balanceSheetData.expense ? this.balanceSheetData.expense : [];
    },
    token() {
      return localStorage.getItem('token')
    },
    noOfDecimals() {
      return this.$route.query.decimals ?? 2
    },
    isModalOpen() {
      return this.$store.state.isModalOpen
    },
    isExpanded() {
      return this.$route.query.expand_all ? !!parseInt(this.$route.query.expand_all) : false;
    }
  },

  methods: {
    exportProfitLoss() {
      let url = `${process.env.VUE_APP_BASE_URL}/export/accounting/income-and-expenditure` + this.getQuery() + `&_token=${this.token}`;
      let a = document.createElement("a")
      a.setAttribute("href", url)
      a.click()
    },
    getTotal(data) {
      let total = 0;
      data.map((i) => {
        total += i.total;
      });
      return total;
    },
    onClickGo(filter) {
      this.getDateWiseProfitLoss(this.getQuery(filter));
    },
    getQuery(filter) {
      const routeQuery = Object.assign({}, this.$route.query)
      const decimals = filter ? filter.decimals : routeQuery.decimals
      const showNonZero = filter ? filter.showNonZero : (routeQuery.show_non_zero ?? 1)

      let query = "?company_id=" + this.companyId;
      if (!this.start && !this.end) return query;
      if (this.start) query += "&start_date=" + this.start;
      if (this.end) query += "&end_date=" + this.end;
      if (this.businessId) query += "&business_id=" + this.businessId;
      if (this.projectId) query += "&project_id=" + this.projectId;
      if (this.donorId) query += "&donor_id=" + this.donorId;
      if (this.costCentreId) query += "&cost_centre_id=" + this.costCentreId;
      if (this.contactProfile) query += "&contact_profile_id=" + this.contactProfile.id;
      if (decimals) query += `&decimals=${decimals}`
      if (showNonZero) query += `&show_non_zero=${showNonZero}`
      return query;
    },
    async getDateWiseProfitLoss(query = "") {
      try {
        this.accountingLoading = true;
        let res = await this.getIncomeExpenditure(query);
        if (!res.status) {
          this.showError(res.response.message);
          return;
        }

        this.balanceSheetData = res.data;
      } catch (err) {
        if (!err.response) {
          this.showError("Something is wrong. Check your connectivity!!");
          return;
        }

        this.showError(err.response.data.message);
      } finally {
        this.accountingLoading = false;
      }
    },

    async getBusinesses() {
      try {
        let companyId = this.$route.params.companyId;
        let res = await this.fetchBusinessList("?company_id=" + companyId);

        if (res.status) {
          this.businesses = res.data;
        }
      } catch (err) {
        if (!err.response) {
          this.showError("Something is wrong. Check your connectivity!!");
          return;
        }

        this.showError(err.response.message);
      } finally {
        this.loading = false;
      }
    },

    onSelectProject(resetDonor = false) {
      this.donors = [];
      if(resetDonor) this.donorId = null;
      for (let project of this.projects) {
        if(project.id === this.projectId && project.party.hasOwnProperty('id')){
          this.donors.push(project.party);
          break;
        }
      }
    },
    async getContactProfiles() {
      try {
        let companyId = this.$route.params.companyId
        let contactId = '';
        let q = '';
        let query = '?company_id=' + companyId + '&q=' + q + '&contact_id' + contactId;
        let res = await this.fetchContactProfiles(query)

        if(res.status) {
          this.contactProfiles = res.data;
        }

      } catch (err) {
        if(!err.response) {
          this.showError('Something is wrong. Check your connectivity!!');
          return;
        }

        this.showError(err.response.message)

      } finally {
        this.loading = false
      }
    },
    onOpenContactSearchModal () {
      this.$store.state.isModalOpenThree = true;
    },
    async getCompanyRoles () {
      const res = await this.fetchCompanyDefaultRoles(`?company_id=${this.$route.params.companyId}`);
      if (res.status) {
        this.companyRoles = res.data
      }
    },
    onSearchContact (contact) {
      this.contactProfile = contact
    },
    onClickFilterButton() {
      this.$store.state.isModalOpen = true
    },
    onFilter (filter) {
      let routeQuery = Object.assign({}, this.$route.query);
      routeQuery.decimals = filter.decimals;
      routeQuery.show_non_zero = filter.showNonZero;
      this.$router.push({path: this.$route.path, query: routeQuery})
      this.onClickGo(filter)
    },
    expandAll() {
      const routeQuery = Object.assign({}, this.$route.query);
      const expandAll = !this.isExpanded;
      routeQuery.expand_all = expandAll ? 1 : 0;
      this.$router.push({path: this.$route.path, query: routeQuery})

      if (this.$refs.assetTree) {
        this.$refs.assetTree.forEach(child => child.expandTree(expandAll));
      }

      if (this.$refs.equityTree) {
        this.$refs.equityTree.forEach(child => child.expandTree(expandAll));
      }
    },
  },

  setup() {
    const showError = inject("showError");
    const showSuccess = inject("showSuccess");
    const store = useStore()

    const { accountingLoading, getIncomeExpenditure } = handleAccounting();

    const { commaFormat } = figureFormatter();

    const { fetchBusinessList } = handleCBusinesses();
    const { fetchProjects } = handleProjects()
    const { fetchCostCentreList } = handleCostCentres()
    const { fetchContactProfiles } = handleContact();
    const { formatPatientLabel } = useAsyncDropdownHelper();
    const { fetchCompanyDefaultRoles } = handleRole();

    return {
      getIncomeExpenditure,
      fetchBusinessList,
      fetchProjects,
      fetchCostCentreList,
      fetchContactProfiles,
      fetchCompanyDefaultRoles,
      accountingLoading,
      commaFormat,
      store,
      showError,
      showSuccess,
      formatPatientLabel
    };
  },

  mounted() {
    // this.getDateWiseBalanceSheet(this.getQuery())
    this.getDateWiseProfitLoss(this.getQuery({showNonZero: true}));
    this.getBusinesses();

    const companyQuery = `?company_id=${this.$route.params.companyId}`
    const projectQuery = companyQuery + '&with_donors=1';

    Promise.all([
      this.fetchProjects(projectQuery).then(res => {
        if(res.data) {
          this.projects = res.data
        }
      }),
      this.fetchCostCentreList(companyQuery).then(res => {
        if(res.data) {
          this.costCentres = res.data
        }
      }),
      this.getCompanyRoles()
    ]).catch(err => {
      this.showError(err.message)
    }).finally(() => {
      this.isDataLoading = false;
    })

    this.getContactProfiles()
  },
};
</script>

<style scoped>
span {
  font-size: 1.2em;
}
</style>
